import React, { useRef, useEffect } from 'react'
import { renderToString } from 'react-dom/server'
import { useIntl } from 'react-intl'
import { useTheme } from 'contexts/theme'
import { useOptionedString } from 'hooks/optioned-string'
import { ModalBody, pickerShadowStyles } from './s'
import 'emoji-picker-element/picker'
import english from 'emoji-picker-element/i18n/en'
import {
    Smile as Smiles,
    User as People,
    Github as Nature,
    Coffee as Foods,
    Dribbble as Activities,
    Map as Places,
    Watch as Objects,
    CheckSquare as Symbols,
    Flag as Flags
} from 'styled-icons/feather'

const EmojiPicker = ({ pickEmoji }) => {
    const {
        locale,
        formatMessage
    } = useIntl()

    const { name: theme } = useTheme()
    const optionize = useOptionedString()

    const picker = useRef(null)

    useEffect(() => {
        picker.current?.addEventListener('emoji-click', ({ detail }) => {
            const id = detail.emoji.shortcodes[0]
            const emoji = detail.unicode

            pickEmoji({
                ...detail,
                id,
                emoji
            })
        })

        picker.current?.i18n = {
            ...english,
            regionLabel: formatMessage({
                id: 'emoji_action_pick',
                defaultMessage: 'Pick emoji'
            }),
            categoriesLabel: formatMessage({
                id: 'noun_categories',
                defaultMessage: 'Categories'
            }),
            categories: {
                'smileys-emotion': formatMessage({
                    id: 'emoji_picker_category_smileys_emoticons',
                    defaultMessage: 'Smileys and emoticons'
                }),
                'people-body': formatMessage({
                    id: 'emoji_picker_category_people_body',
                    defaultMessage: 'People and body'
                }),
                'animals-nature': formatMessage({
                    id: 'emoji_picker_category_animals_nature',
                    defaultMessage: 'Animals and nature'
                }),
                'food-drink': formatMessage({
                    id: 'emoji_picker_category_food_drink',
                    defaultMessage: 'Food and drink'
                }),
                'travel-places': formatMessage({
                    id: 'emoji_picker_category_travel_places',
                    defaultMessage: 'Travel and places'
                }),
                activities: formatMessage({
                    id: 'emoji_picker_category_activities',
                    defaultMessage: 'Activities'
                }),
                objects: formatMessage({
                    id: 'emoji_picker_category_objects',
                    defaultMessage: 'Objects'
                }),
                symbols: formatMessage({
                    id: 'emoji_picker_category_symbols',
                    defaultMessage: 'Symbols'
                }),
                flags: formatMessage({
                    id: 'emoji_picker_category_flags',
                    defaultMessage: 'Flags'
                })
            },
            searchDescription: formatMessage({
                id: 'emoji_picker_search_explainer',
                defaultMessage: 'Navigate the search results by pressing up or down, and pick an emoji by pressing enter.'
            }),
            searchLabel: optionize(formatMessage({
                id: 'emoji_action_find',
                defaultMessage: 'Find emoji'
            })),
            searchResultsLabel: formatMessage({
                id: 'noun_search_result',
                defaultMessage: 'Search result'
            }),
            favoritesLabel: formatMessage({
                id: 'noun_favorites',
                defaultMessage: 'Favorites'
            }),
            loadingMessage: formatMessage({
                id: 'message_loading',
                defaultMessage: 'Loading...'
            }),
            networkErrorMessage: formatMessage({
                id: 'error_network',
                defaultMessage: 'Could not reach the server'
            })
        }

        const shadowStyle = document.createElement('style')
        shadowStyle.textContent = pickerShadowStyles
        picker.current?.shadowRoot.appendChild(shadowStyle)

        picker.current?.shadowRoot.querySelectorAll('.nav-button').forEach((button, index) => {
            const Icon = categoryIcons[index]
            button.innerHTML = renderToString(<Icon size={20} />)
        })

        picker.current?.shadowRoot.querySelector('.tabpanel').setAttribute('tabindex', -1)
    }, [picker, locale])

    return (
        <ModalBody>
            <emoji-picker
                emoji-version="15.0"
                locale={locale}
                skin-tone-emoji="👍"
                {...(theme !== 'system' ? { class: theme } : null)}
                ref={picker} />
        </ModalBody>
    )
}

const categoryIcons = [
    Smiles,
    People,
    Nature,
    Foods,
    Places,
    Activities,
    Objects,
    Symbols,
    Flags
]

export default EmojiPicker